/** Row wrapper with roving-tabindex + aria-current when keyboard nav is on. ARIA 1.2 §6.27 forbids interactive descendants inside `role="option"`, so we use aria-current + plain ul/li instead of the listbox pattern. */

import type { ReactNode } from "react";
import styles from "./SidePanelNotificationList.module.css";

export interface KbdRowProps {
  id: string;
  enabled: boolean;
  selected: boolean;
  /** Mid-exit-animation: forces aria-hidden + tabIndex=-1 on the wrapper (WCAG 2.1 SC 2.4.3). */
  dismissing?: boolean;
  as?: "li" | "div";
  children: ReactNode;
}

export function KbdRow({
  id,
  enabled,
  selected,
  dismissing,
  as = "div",
  children,
}: KbdRowProps): ReactNode {
  const kbdAttrs = enabled
    ? ({
        id: `kbd-row-${id}`,
        className: styles.kbdRow,
        // `dismissAttrs` (spread after `kbdAttrs`) forces tabIndex=-1 when
        // dismissing, so the `dismissing ?` branch here would be dead code.
        tabIndex: selected ? 0 : -1,
        "aria-current": selected && !dismissing ? ("true" as const) : undefined,
      } as const)
    : null;
  const dismissAttrs = dismissing ? { "aria-hidden": true as const, tabIndex: -1 } : null;
  // `as="li"`: wrapper is always rendered (list semantics).
  if (as === "li") {
    return (
      <li {...(kbdAttrs ?? {})} {...(dismissAttrs ?? {})}>
        {children}
      </li>
    );
  }
  // `as="div"` + `enabled=false`: drop wrapper unless dismissing (need it for aria-hidden).
  if (!kbdAttrs) {
    if (!dismissAttrs) return children;
    return <div {...dismissAttrs}>{children}</div>;
  }
  return (
    <div {...kbdAttrs} {...(dismissAttrs ?? {})}>
      {children}
    </div>
  );
}
